import { t as tr } from '../i18n.js'
import { useMemo, useState, type FormEvent } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, ApiError, browserTimezone, type ApprovalPeriod, type Overlap, type Project, type Task, type TimeEntry } from '../api.js'
import { isClosed } from './activeOnly.js'
import { useAuth } from '../auth.js'
import { fmtElapsed, useTimer } from '../timer.js'
import { Section } from '../section.js'
import { ChevronLeft, ChevronRight, Pause, Play, Square } from 'lucide-react'
import { createEntryOfflineTolerant } from '../offline.js'
import { addDays, fmtDay, fmtMinutes, instantToZoned, mondayOf, todayIn, zonedToInstant } from '../time.js'

export function TimePage() {
  const { user } = useAuth()
  const zone = user?.timezone ?? 'UTC'
  const [weekStart, setWeekStart] = useState(() => mondayOf(todayIn(zone)))
  const weekEnd = addDays(weekStart, 6)
  const qc = useQueryClient()
  const entries = useQuery({
    queryKey: ['time', weekStart],
    queryFn: () => api<{ entries: TimeEntry[]; totalMinutes: number }>(`/time-entries?from=${weekStart}&to=${weekEnd}`),
  })
  const projects = useQuery({ queryKey: ['projects', false], queryFn: () => api<{ projects: Project[] }>('/projects') })
  const approvals = useQuery({ queryKey: ['approvals', 'mine', 13], queryFn: () => api<{ periods: ApprovalPeriod[] }>('/approvals/mine?weeksBack=13') })
  const lockedKeys = new Set((approvals.data?.periods ?? []).filter((p) => p.state === 'approved').map((p) => `${p.projectId}:${p.weekStart}`))
  const isLocked = (e: TimeEntry) => lockedKeys.has(`${e.projectId}:${e.projectWeekStart}`)
  const [correcting, setCorrecting] = useState<TimeEntry | null>(null)
  const loggable = (projects.data?.projects ?? []).filter((p) => p.myRole && p.myRole !== 'viewer' && !p.archivedAt)
  const timer = useTimer()
  const [editing, setEditing] = useState<TimeEntry | null>(null)
  const [draftDate, setDraftDate] = useState<string | null>(null)
  const invalidate = () => { void qc.invalidateQueries({ queryKey: ['approvals'] }); return qc.invalidateQueries({ queryKey: ['time'] }) }
  const del = useMutation({ mutationFn: (id: string) => api(`/time-entries/${id}`, { method: 'DELETE' }), onSuccess: invalidate })

  const days = useMemo(() => Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)), [weekStart])
  const calStatus = useQuery({ queryKey: ['calendar-status'], queryFn: () => api<{ available: boolean; connected: boolean }>('/calendar/status') })
  const todayStr = todayIn(zone)
  const sugg = useQuery({ queryKey: ['suggestions', todayStr], queryFn: () => api<{ connected: boolean; suggestions: { externalId: string; title: string; startedAt: string; endedAt: string; minutes: number }[] }>(`/time/suggestions?date=${todayStr}`), enabled: !!calStatus.data?.connected && days.includes(todayStr), retry: false })
  const [prefill, setPrefill] = useState<{ startedAt: string; endedAt: string; notes: string } | null>(null)
  const byDay = new Map<string, TimeEntry[]>()
  for (const e of entries.data?.entries ?? []) byDay.set(e.userLocalDate, [...(byDay.get(e.userLocalDate) ?? []), e])
  const browserZone = browserTimezone()

  return (
    <>
      <h1>{tr("Time")}</h1>
      <Section id="time-timers" title="Timers" defaultOpen subtitle={timer.timers.length ? `${timer.timers.length} timer${timer.timers.length === 1 ? '' : 's'}` : undefined}>
        <ul className="help small muted">
          <li>One timer per project; only one runs at a time — starting another pauses it.</li>
          <li>{tr("Set the task and a note when you start, or edit them in the list.")}</li>
          <li><strong>Stop &amp; save</strong> writes one entry for the total time; <strong>{tr("Discard")}</strong> throws the timer away.</li>
        </ul>
        {loggable.length === 0 && projects.data && <p className="notice small">{tr("You are not a member of any project yet, so there is nowhere to log time. Ask a project administrator to add you.")}</p>}
        {timer.timers.length > 0 && (
          <table className="table">
            <thead><tr><th>{tr("Project")}</th><th>{tr("Task")}</th><th>{tr("Note")}</th><th className="num">{tr("Elapsed")}</th><th></th></tr></thead>
            <tbody>{timer.timers.map((tm) => <TimerRow key={tm.projectId} tm={tm} />)}</tbody>
          </table>
        )}
        {loggable.length > 0 && <NewTimerForm projects={loggable} />}
      </Section>

      <Section id="time-week" title="Week" defaultOpen subtitle={`${fmtDay(weekStart)} – ${fmtDay(weekEnd)} · total ${fmtMinutes(entries.data?.totalMinutes ?? 0)}`}
        actions={<span className="row" style={{ gap: '.35rem' }}>
          <button className="button sm" data-tip={tr("Previous week")} onClick={() => setWeekStart(addDays(weekStart, -7))}><ChevronLeft size={14} aria-hidden /> {tr("Prev")}</button>
          <button className="button sm" data-tip={tr("Jump to the current week")} onClick={() => setWeekStart(mondayOf(todayIn(zone)))}>This week</button>
          <button className="button sm" data-tip={tr("Next week")} onClick={() => setWeekStart(addDays(weekStart, 7))}>Next <ChevronRight size={14} aria-hidden /></button>
        </span>}>
        <ul className="help small muted">
          <li>{tr("Your week is shown in")} <strong>{zone}</strong>; totals per project are measured in each project's own timezone.</li>
          <li>{tr("Click")} <strong>{tr("+ Add")}</strong> on a day to log time; entries in approved weeks are locked and offer <strong>{tr("Correct…")}</strong> instead.</li>
          {browserZone !== zone && <li className="bad">Your browser is in {browserZone} but your profile timezone is {zone}. Times are entered and shown in {zone}.</li>}
        </ul>
      {sugg.data && sugg.data.suggestions.length > 0 && (
        <section className="card stack">
          <h2>{tr("From your calendar today")}</h2>
          <div className="row">{sugg.data.suggestions.map((s) => <button key={s.externalId} className="button" onClick={() => { setEditing(null); setCorrecting(null); setDraftDate(null); setPrefill({ startedAt: s.startedAt, endedAt: s.endedAt, notes: s.title }) }}>{instantToZoned(s.startedAt, zone).time}–{instantToZoned(s.endedAt, zone).time} {s.title} <span className="muted small">({fmtMinutes(s.minutes)})</span></button>)}</div>
        </section>
      )}
      {(editing || draftDate || correcting || prefill) && (
        <EntryForm
          key={editing?.id ?? (correcting ? `c-${correcting.id}` : prefill ? `p-${prefill.startedAt}` : draftDate ?? 'new')}
          zone={zone} projects={loggable} entry={editing} defaultDate={draftDate ?? undefined} corrects={correcting} prefill={prefill}
          onDone={() => { setEditing(null); setDraftDate(null); setCorrecting(null); setPrefill(null); void invalidate(); void qc.invalidateQueries({ queryKey: ['suggestions'] }) }}
          onCancel={() => { setEditing(null); setDraftDate(null); setCorrecting(null); setPrefill(null) }}
        />
      )}

      {days.map((d) => {
        const list = byDay.get(d) ?? []
        const total = list.reduce((a, e) => a + e.durationMinutes, 0)
        return (
          <section className="card day" key={d}>
            <div className="row between">
              <h2 style={{ margin: 0 }}>{fmtDay(d)} <span className="muted" style={{ textTransform: 'none', letterSpacing: 0 }}>· {fmtMinutes(total)}</span></h2>
              {loggable.length > 0 && <button className="link" onClick={() => { setEditing(null); setDraftDate(d) }}>+ Add</button>}
            </div>
            {list.length > 0 && (
              <table className="table">
                <tbody>
                  {list.map((e) => {
                    const s = instantToZoned(e.startedAt, zone), t = instantToZoned(e.endedAt, zone)
                    return (
                      <tr key={e.id}>
                        <td className="mono" style={{ whiteSpace: 'nowrap' }}>{s.time}–{t.time}{t.date !== s.date && <sup>+1</sup>}</td>
                        <td><strong>{e.projectCode}</strong> <span className="muted">{e.projectName}</span>{e.taskTitle && <> · {e.taskTitle}</>}{e.notes && <div className="small muted">{e.notes}</div>}</td>
                        <td className="mono" style={{ textAlign: 'right' }}>{fmtMinutes(e.durationMinutes)}{!e.billable && <span className="tag">{tr("non-billable")}</span>}</td>
                        <td style={{ whiteSpace: 'nowrap' }}>
                          {isLocked(e)
                            ? <><span className="muted small" title="This week is approved and locked">{tr("🔒 approved")}</span>{' '}<button className="link" onClick={() => { setEditing(null); setDraftDate(null); setCorrecting(e) }}>Correct…</button></>
                            : <><button className="link" onClick={() => { setDraftDate(null); setCorrecting(null); setEditing(e) }}>Edit</button>{' '}
                              <button className="link danger" onClick={() => del.mutate(e.id)}>Delete</button></>}
                          {e.correctsEntryId && <span className="tag" title="Correction of an entry in an approved week">{tr("correction")}</span>}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            )}
          </section>
        )
      })}
      {del.isError && <p className="bad">{(del.error as ApiError).message}</p>}
      </Section>
    </>
  )
}

function TimerRow({ tm }: { tm: import('../api.js').ProjectTimer }) {
  const timer = useTimer()
  const tasks = useQuery({ queryKey: ['tasks', tm.projectId], queryFn: () => api<{ tasks: Task[] }>(`/projects/${tm.projectId}/tasks`) })
  const [note, setNote] = useState(tm.notes ?? '')
  return (
    <tr className={tm.running ? 'running' : ''}>
      <td>{tm.running && <span className="dot" />} <strong>{tm.projectCode}</strong> <span className="muted small">{tm.projectName}</span></td>
      <td><select value={tm.taskId ?? ''} data-tip={tr("Task this timer is for (optional)")} onChange={(e) => void timer.update(tm.projectId, { taskId: e.target.value || null })}>
        <option value="">{tr("— none —")}</option>{(tasks.data?.tasks ?? []).filter((t) => t.kind !== 'checklist' && (!isClosed(t.statusCategory) || t.id === tm.taskId)).map((t) => <option key={t.id} value={t.id}>{'\u00a0'.repeat(t.depth * 2)}{t.key} {t.title}</option>)}</select></td>
      <td><input value={note} placeholder={tr("Note")} data-tip={tr("Note saved onto the time entry when you stop")} onChange={(e) => setNote(e.target.value)} onBlur={() => { if (note !== (tm.notes ?? '')) void timer.update(tm.projectId, { notes: note || null }) }} /></td>
      <td className="num mono">{fmtElapsed(timer.elapsedFor(tm))}</td>
      <td style={{ whiteSpace: 'nowrap' }}>
        {tm.running
          ? <button className="button" data-tip={tr("Pause this timer; time is kept")} onClick={() => void timer.pause(tm.projectId)}><Pause size={14} aria-hidden /> {tr("Pause")}</button>
          : <button className="button" data-tip={tr("Resume this timer (pauses the running one)")} onClick={() => void timer.start({ projectId: tm.projectId })}><Play size={14} aria-hidden /> {tr("Resume")}</button>}{' '}
        <button className="button primary" data-tip={tr("Stop and save one time entry for the total on this project")} onClick={() => void timer.stop(tm.projectId, { notes: note || null })}><Square size={14} aria-hidden /> Stop &amp; save</button>{' '}
        <button className="button danger" data-tip={tr("Throw this timer away without saving")} onClick={() => { if (confirm(`Discard the ${tm.projectCode} timer?`)) void timer.stop(tm.projectId, { discard: true }) }}>Discard</button>
      </td>
    </tr>
  )
}

function NewTimerForm({ projects }: { projects: Project[] }) {
  const timer = useTimer()
  const [projectId, setProjectId] = useState(projects[0]?.id ?? '')
  const [taskId, setTaskId] = useState('')
  const [notes, setNotes] = useState('')
  const tasks = useQuery({ queryKey: ['tasks', projectId], queryFn: () => api<{ tasks: Task[] }>(`/projects/${projectId}/tasks`), enabled: !!projectId })
  return (
    <form className="row newtimer" onSubmit={(e) => { e.preventDefault(); void timer.start({ projectId, taskId: taskId || null, notes: notes || null }); setNotes(''); setTaskId('') }}>
      <strong className="small">{tr("New timer")}</strong>
      <select value={projectId} onChange={(e) => { setProjectId(e.target.value); setTaskId('') }} data-tip={tr("Which project the new timer is for")}>{projects.map((p) => <option key={p.id} value={p.id}>{p.code} — {p.name}</option>)}</select>
      <select value={taskId} onChange={(e) => setTaskId(e.target.value)} data-tip={tr("Task (optional)")}><option value="">{tr("— no task —")}</option>{(tasks.data?.tasks ?? []).filter((t) => t.kind !== 'checklist' && !isClosed(t.statusCategory)).map((t) => <option key={t.id} value={t.id}>{'\u00a0'.repeat(t.depth * 2)}{t.key} {t.title}</option>)}</select>
      <input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder={tr("Note (optional)")} style={{ flex: 1, minWidth: '10rem' }} data-tip={tr("Note saved onto the entry")} />
      <button className="button primary" data-tip={tr("Start a timer for this project (pauses any running timer)")}><Play size={14} aria-hidden /> {tr("Start timer")}</button>
      {timer.error && <span className="bad small">{timer.error}</span>}
    </form>
  )
}

function EntryForm({ zone, projects, entry, defaultDate, corrects, prefill, onDone, onCancel }: {
  zone: string; projects: Project[]; entry: TimeEntry | null; defaultDate?: string | undefined; corrects?: TimeEntry | null; prefill?: { startedAt: string; endedAt: string; notes: string } | null; onDone: () => void; onCancel: () => void
}) {
  const start = entry ? instantToZoned(entry.startedAt, zone) : prefill ? instantToZoned(prefill.startedAt, zone) : null
  const end = entry ? instantToZoned(entry.endedAt, zone) : prefill ? instantToZoned(prefill.endedAt, zone) : null
  const [projectId, setProjectId] = useState(entry?.projectId ?? corrects?.projectId ?? projects[0]?.id ?? '')
  const [taskId, setTaskId] = useState(entry?.taskId ?? '')
  const tasks = useQuery({ queryKey: ['tasks', projectId], queryFn: () => api<{ tasks: Task[] }>(`/projects/${projectId}/tasks`), enabled: !!projectId })
  const [date, setDate] = useState(start?.date ?? defaultDate ?? todayIn(zone))
  const [from, setFrom] = useState(start?.time ?? '09:00')
  const [to, setTo] = useState(end?.time ?? '10:00')
  const [notes, setNotes] = useState(entry?.notes ?? prefill?.notes ?? '')
  const [billable, setBillable] = useState(entry?.billable ?? true)
  const [err, setErr] = useState<string | null>(null)
  const [overlaps, setOverlaps] = useState<Overlap[]>([])

  const save = useMutation({
    mutationFn: () => {
      const startedAt = zonedToInstant(date, from, zone)
      // An end time earlier than the start means it crossed midnight.
      const endedAt = to > from ? zonedToInstant(date, to, zone) : zonedToInstant(addDays(date, 1), to, zone)
      const body = { projectId, taskId: taskId || null, startedAt, endedAt, notes: notes || null, billable, ...(corrects ? { correctsEntryId: corrects.id } : {}) }
      if (entry) return api<{ entry: TimeEntry; overlaps: Overlap[] }>(`/time-entries/${entry.id}`, { method: 'PATCH', json: body }).then((r) => ({ ...r, queued: false }))
      return createEntryOfflineTolerant(body) as Promise<{ entry?: TimeEntry; overlaps?: Overlap[]; queued: boolean }>
    },
    onSuccess: (r) => { if (r.queued) { setErr(null); onDone(); return } if (r.overlaps && r.overlaps.length) setOverlaps(r.overlaps); else onDone() },
    onError: (e) => setErr(e instanceof ApiError ? e.message : 'Could not save'),
  })
  const project = projects.find((p) => p.id === projectId)

  return (
    <form className="card stack entryform" onSubmit={(e: FormEvent) => { e.preventDefault(); setErr(null); setOverlaps([]); save.mutate() }}>
      <h2>{entry ? 'Edit entry' : corrects ? 'Correction entry' : 'New entry'}</h2>
      {corrects && <p className="small notice">Adjusts the locked entry of {instantToZoned(corrects.startedAt, zone).date} ({fmtMinutes(corrects.durationMinutes)} on {corrects.projectCode}). Enter the difference as a new entry dated in an open week — the original stays as approved.</p>}
      <div className="row">
        <label style={{ flex: 2 }}>{tr("Project")}
          <select value={projectId} onChange={(e) => { setProjectId(e.target.value); setTaskId('') }} required>
            {projects.map((p) => <option key={p.id} value={p.id}>{p.code} — {p.name}</option>)}
          </select>
          {project && <span className="hint">Weeks on this project close Monday 00:00 {project.timezoneOfTruth}.</span>}
        </label>
        <label style={{ flex: 2 }}>{tr("Task")} <span className="muted small">(optional)</span>
          <select value={taskId} onChange={(e) => setTaskId(e.target.value)}>
            <option value="">{tr("— none —")}</option>
            {(tasks.data?.tasks ?? []).filter((t) => !isClosed(t.statusCategory) || t.id === entry?.taskId).map((t) => (
              <option key={t.id} value={t.id}>{'\u00a0'.repeat(t.depth * 2)}{t.key} {t.title}</option>
            ))}
          </select>
        </label>
        <label>{tr("Date")}<input type="date" value={date} onChange={(e) => setDate(e.target.value)} required /></label>
        <label>{tr("From")}<input type="time" value={from} onChange={(e) => setFrom(e.target.value)} required /></label>
        <label>{tr("To")}<input type="time" value={to} onChange={(e) => setTo(e.target.value)} required /></label>
      </div>
      <label>{tr("Notes")}<input value={notes} onChange={(e) => setNotes(e.target.value)} maxLength={5000} placeholder={tr("What did you work on?")} /></label>
      <label className="inline"><input type="checkbox" checked={billable} onChange={(e) => setBillable(e.target.checked)} /> Billable</label>
      {err && <p className="bad">{err}</p>}
      {overlaps.length > 0 && (
        <p className="notice small">Saved — but this overlaps {overlaps.length} other {overlaps.length === 1 ? 'entry' : 'entries'} ({overlaps.map((o) => o.projectCode).join(', ')}). Overlaps are allowed; double-check the times. <button type="button" className="link" onClick={onDone}>OK</button></p>
      )}
      <div className="row"><button className="button primary" disabled={save.isPending}>{entry ? tr("Save") : tr("Add entry")}</button><button type="button" className="button" onClick={onCancel}>{tr("Cancel")}</button></div>
    </form>
  )
}
